"use client";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import Image from "next/image";
import { useMotion } from "@/contexts/MotionContext";
import { TEAM_MEMBERS } from "@/lib/constants";

interface TeamCursor {
  id: string;
  name: string;
  image: string;
  startX: number;
  pattern: {
    x: number[];
    y: number[];
  };
  color: string;
}

interface LabsCursorsAnimationProps {
  height?: string;
  maxHeight?: string;
  width?: string;
  numCursors?: number;
  constrainToRight?: boolean;
}

// Predefined patterns for more varied movement
const PATTERNS = [
  {
    x: [0, 100, 0],
    y: [0, 50, 100],
  },
  {
    x: [100, 0, 100],
    y: [100, 50, 0],
  },
  {
    x: [50, 100, 0],
    y: [50, 0, 100],
  },
  {
    x: [0, 50, 100],
    y: [100, 0, 50],
  },
  {
    x: [100, 50, 0],
    y: [0, 100, 50],
  },
  {
    x: [0, 100, 50],
    y: [50, 100, 0],
  },
  {
    x: [100, 0, 50],
    y: [0, 100, 50],
  },
  {
    x: [50, 0, 100],
    y: [100, 50, 0],
  },
];

// Right-side constrained patterns (x values between 50-100%)
const RIGHT_PATTERNS = [
  {
    x: [60, 90, 70],
    y: [0, 50, 100],
  },
  {
    x: [90, 60, 85],
    y: [100, 50, 0],
  },
  {
    x: [70, 95, 65],
    y: [50, 0, 100],
  },
  {
    x: [65, 85, 75],
    y: [100, 0, 50],
  },
  {
    x: [80, 60, 90],
    y: [0, 100, 50],
  },
  {
    x: [75, 95, 70],
    y: [50, 100, 0],
  },
  {
    x: [85, 65, 90],
    y: [0, 100, 50],
  },
  {
    x: [70, 85, 75],
    y: [100, 50, 0],
  },
];

// Helper to get random pattern
const getRandomPattern = (constrainToRight = false) => {
  const patterns = constrainToRight ? RIGHT_PATTERNS : PATTERNS;
  return patterns[Math.floor(Math.random() * patterns.length)];
};

// Helper to get random team members
const getRandomTeamMembers = (count: number) => {
  const shuffled = [...TEAM_MEMBERS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

// Convert TEAM_MEMBERS to cursor format
const generateCursors = (
  count: number,
  constrainToRight = false,
): TeamCursor[] => {
  const randomMembers = getRandomTeamMembers(count);
  return randomMembers.map((member, index) => {
    const pattern = getRandomPattern(constrainToRight);
    return {
      id: member.id,
      name: member.name,
      image: member.image,
      startX: pattern.x[0],
      pattern: {
        x: pattern.x,
        y: pattern.y,
      },
      color: `text-brand-${["teal", "pink", "yellow"][index % 3]}`,
    };
  });
};

export default function LabsCursorsAnimation({
  height = "20rem",
  maxHeight = "30rem",
  width = "100%",
  numCursors = 3,
  constrainToRight = false,
}: LabsCursorsAnimationProps) {
  const { isMotionEnabled } = useMotion();
  const [isClient, setIsClient] = useState(false);
  const [teamCursors, setTeamCursors] = useState<TeamCursor[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsClient(true);
    setTeamCursors(generateCursors(numCursors, constrainToRight));

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [numCursors, constrainToRight]);

  if (!isMotionEnabled || !isClient) {
    return null;
  }

  return (
    <div
      className="relative pointer-events-none"
      style={{
        height: isMobile ? "12.5rem" : height,
        maxHeight,
        width,
      }}
    >
      <div className="absolute inset-0">
        {teamCursors.map((cursor, i) => (
          <motion.div
            key={cursor.id}
            className="absolute left-0 top-0 w-full h-full pointer-events-none"
            initial={{
              x: `${cursor.startX}%`,
              y: `${cursor.pattern.y[0]}%`,
              opacity: 0,
            }}
            animate={{
              x: cursor.pattern.x.map((x) => `${x}%`),
              y: cursor.pattern.y.map((y) => `${y}%`),
              opacity: 1,
            }}
            transition={{
              x: {
                duration: 12,
                repeat: Infinity,
                repeatType: "mirror",
                ease: "easeInOut",
                delay: i * 1.2,
              },
              y: {
                duration: 12,
                repeat: Infinity,
                repeatType: "mirror",
                ease: "easeInOut",
                delay: i * 1.2,
              },
              opacity: {
                duration: 0.5,
                delay: i * 0.1,
              },
            }}
          >
            <div className="flex items-center gap-2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className={`${cursor.color} pointer-events-none`}>
                <div className="w-0 h-0 border-8 border-transparent border-t-current transform -rotate-45" />
              </div>
              <div className="flex items-center gap-2 bg-white/90 dark:bg-gray-900/90 rounded-full px-3 py-1 shadow-sm pointer-events-none">
                <Image
                  src={cursor.image}
                  alt={cursor.name}
                  width={24}
                  height={24}
                  className="rounded-full"
                />
                <span className="text-sm font-medium">
                  {cursor.name.split(" ")[0]}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
